import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AdCreative, FBAdPreviewFormat } from '@marketing-milk/interfaces'
import { useGetBusinessID } from '@marketing-milk/frontend'
import {
  invalidateCreativePreview,
  prefetchCreativePreview,
} from 'hooks/creatives/fetch-creative-preview'
import { isDynamicCreative } from '../../../campaigns/helpers/campaign.helpers'
import { SurfacePalette } from '../../tokens'
import { CreativePreviewModal } from '../../components/CreativePreviewModal'

interface CreativePreviewActions {
  /** Edit-mode only: open the creative's native editor (new tab). */
  onOpenSource?: (creative: AdCreative) => void
  /** Record an AE-attested client approval (opens the approval modal). */
  onApprove?: (creative: AdCreative) => void
  /** Clear an existing approval (opens the remove-confirm modal). */
  onRemoveApproval?: (creative: AdCreative) => void
}

interface CreativePreviewSource {
  /** The live sprint-creatives list; the modal renders the matching row by id. */
  creatives?: AdCreative[]
  /** Refetch the creatives so the modal's local fallback can't attest stale copy. */
  refetchCreatives?: () => Promise<unknown>
  /**
   * Ordered ads to cycle through with prev/next arrows in the preview modal —
   * e.g. Present passes just the current slide's group/ungrouped bucket, so
   * arrows follow the same ads the client is looking at on screen. Omit to
   * leave the modal's nav arrows off entirely (Edit's card-click preview has
   * no single natural "next" ad across the whole grouped-by-theme layout).
   */
  navList?: AdCreative[]
}

/**
 * Shared "Preview ad" interaction for the Edit (`CreativeSection`) and Present
 * (`PresentCreativeSection`) creative grids. Owns the preview-modal state and
 * returns the card callback plus the modal to render. Mirrors
 * `useCreativeApproval`: `onModalOpenChange` lets Present lock deck navigation so
 * Arrow/Esc don't move slides or exit the deck while the preview is open.
 *
 * The preview is the only surface that shows the full ad (copy + CTA), so the
 * meeting-time actions live in its header bar. `actions` forwards the host's
 * approve / remove / open-source handlers into the modal. Approve and Remove
 * close the preview first so their (centered) confirm modal doesn't stack on top
 * of this (centered) one.
 *
 * We track the previewing creative by **id** and derive the live row from the
 * passed `creatives` list (rather than snapshotting the object), and refetch the
 * list on open. That keeps the modal's local (Facebook-independent) fallback
 * rendering current fields — so when the live FB preview is down, the AE can
 * approve from the local render without the risk of attesting stale, since-edited
 * copy (see `CreativePreviewModal`'s approve gate).
 *
 * `prefetch` warms a single creative's live preview into the shared cache (used
 * on card hover) so the modal opens instantly. We warm one creative at a time
 * on hover rather than all on mount to avoid ~7 simultaneous live FB fetches.
 */
export function useCreativePreview(
  palette: SurfacePalette,
  onModalOpenChange?: (open: boolean) => void,
  actions: CreativePreviewActions = {},
  source: CreativePreviewSource = {}
) {
  const businessID = useGetBusinessID()
  const { creatives, refetchCreatives, navList } = source
  const [previewingId, setPreviewingId] = useState<number | undefined>(undefined)
  // Flips true once the on-open creatives refetch settles; gates the static
  // local-fallback approval so it never acts on pre-refetch fields.
  const [creativeFresh, setCreativeFresh] = useState(false)
  // True when the latest refetch failed (vs still in flight). Gates the dynamic
  // asset-pool approval (which has no FB preview to fall back on) and surfaces a
  // retry banner + "Latest assets could not be loaded." approve tooltip.
  const [refetchFailed, setRefetchFailed] = useState(false)
  const previewing =
    previewingId !== undefined ? creatives?.find(c => c.id === previewingId) : undefined
  const open = previewingId !== undefined
  const previewingIsDynamic = !!previewing && isDynamicCreative(previewing)

  useEffect(() => {
    onModalOpenChange?.(open)
    return () => onModalOpenChange?.(false)
  }, [open, onModalOpenChange])

  // Refetch the creatives so the fallback / asset pool reflects the latest fields.
  // Held in a ref so a non-stable refetch identity can't retrigger the effect. A
  // monotonic token drops stale resolutions (a slow open-refetch landing after a
  // focus-refetch, or after close).
  //
  // `creativeFresh` flips true once the refetch *settles* (success or failure) —
  // this preserves the static single-image/-video local-fallback approval path,
  // which intentionally lets the AE approve the refetched-or-cached fields even
  // when the network is down. `refetchFailed` separately records a failure;
  // dynamic (which has no FB preview to fall back on) gates approval on a
  // *successful* refetch via `creativeFresh && !refetchFailed`. react-query 3's
  // `refetch()` resolves with a result object carrying the error rather than
  // rejecting, so an `isError` result (or a thrown rejection) marks failure.
  const refetchRef = useRef(refetchCreatives)
  refetchRef.current = refetchCreatives
  const reqIdRef = useRef(0)
  const runRefetch = useCallback(() => {
    const reqId = ++reqIdRef.current
    setCreativeFresh(false)
    setRefetchFailed(false)
    Promise.resolve(refetchRef.current?.())
      .then(result => {
        if (reqId === reqIdRef.current && result && (result as { isError?: boolean }).isError) {
          setRefetchFailed(true)
        }
      })
      .catch(() => {
        if (reqId === reqIdRef.current) setRefetchFailed(true)
      })
      .finally(() => {
        if (reqId === reqIdRef.current) setCreativeFresh(true)
      })
  }, [])

  useEffect(() => {
    if (previewingId === undefined) {
      // Invalidate any in-flight refetch so it can't flip state after close.
      reqIdRef.current++
      setCreativeFresh(false)
      setRefetchFailed(false)
      return
    }
    runRefetch()
  }, [previewingId, runRefetch])

  // Opening a dynamic creative in the Wizard can change its asset pool. When the
  // packet regains focus/visibility, re-run the refetch so the pool refreshes in
  // place — marking it stale (approval disabled) until the refresh succeeds. The
  // FB path handles its own focus refresh inside the modal; this covers dynamic,
  // which has no live FB preview to refresh.
  useEffect(() => {
    if (previewingId === undefined || !previewingIsDynamic) return
    const onFocus = () => runRefetch()
    const onVisible = () => {
      if (document.visibilityState === 'visible') runRefetch()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [previewingId, previewingIsDynamic, runRefetch])

  // If the previewing creative disappears after a refetch (deleted / removed from
  // the sprint), close the modal so deck nav unlocks and we don't render a stale id.
  useEffect(() => {
    if (previewingId !== undefined && creativeFresh && creatives && !previewing) {
      setPreviewingId(undefined)
    }
  }, [previewingId, creativeFresh, creatives, previewing])

  const onPreview = useCallback((creative: AdCreative) => setPreviewingId(creative.id), [])

  // Prev/next arrows step through `navList` in order, wherever the currently
  // previewing ad falls in it — reusing the same open/refetch path as clicking a
  // different card (`setPreviewingId`), just without a card click.
  const navIndex =
    navList && previewingId !== undefined ? navList.findIndex(c => c.id === previewingId) : -1
  const hasPrev = navIndex > 0
  const hasNext = navIndex >= 0 && navIndex < (navList?.length ?? 0) - 1
  const goToPrev = useCallback(() => {
    const prev = navIndex > 0 ? navList?.[navIndex - 1] : undefined
    if (prev) setPreviewingId(prev.id)
  }, [navList, navIndex])
  const goToNext = useCallback(() => {
    const next = navIndex >= 0 ? navList?.[navIndex + 1] : undefined
    if (next) setPreviewingId(next.id)
  }, [navList, navIndex])

  const prefetch = useCallback(
    (creative: AdCreative) => {
      // Dynamic creatives never hit the live FB preview (they render the hydrated
      // asset pool), so warming an FB preview for them would be a wasted call.
      if (isDynamicCreative(creative)) return
      if (creative.id) {
        prefetchCreativePreview(businessID, creative.id, FBAdPreviewFormat.MOBILE_FEED_STANDARD)
      }
    },
    [businessID]
  )

  const { onApprove, onRemoveApproval, onOpenSource: openSource } = actions
  // Opening the native editor (new tab) means the creative may be edited; drop the
  // cached preview now so the modal's focus-refetch (and any later open) is live,
  // not the pre-edit HTML.
  const onOpenSource = openSource
    ? (creative: AdCreative) => {
        if (creative.id) {
          invalidateCreativePreview(businessID, creative.id, FBAdPreviewFormat.MOBILE_FEED_STANDARD)
        }
        openSource(creative)
      }
    : undefined
  // Approve/Remove open their own centered modal — close the preview first to
  // avoid stacking two centered dialogs. Open-source opens a new tab, so the
  // preview can stay up.
  const closeThen = (handler?: (creative: AdCreative) => void) =>
    handler
      ? (creative: AdCreative) => {
          setPreviewingId(undefined)
          handler(creative)
        }
      : undefined

  const modals = previewing && (
    <CreativePreviewModal
      creative={previewing}
      creativeFresh={creativeFresh}
      refetchFailed={refetchFailed}
      onRetryFresh={runRefetch}
      palette={palette}
      onClose={() => setPreviewingId(undefined)}
      onApprove={closeThen(onApprove)}
      onRemoveApproval={closeThen(onRemoveApproval)}
      onOpenSource={onOpenSource}
      hasPrev={hasPrev}
      hasNext={hasNext}
      onPrev={navList ? goToPrev : undefined}
      onNext={navList ? goToNext : undefined}
    />
  )

  return { onPreview, prefetch, modals }
}
